import { WorkCase } from '@/data/types';

const novatimepuncher: WorkCase = {
  id: "novatimepuncher",
  slug: "novatimepuncher",
  title: "NT Puncher: Punch Clock for Mac & iPhone",
  description: "Native app for Mac and iPhone for clocking in and out of the NovaTime time tracking system, built with SwiftUI, a glass UI and animated SF Symbols. Behind it lies the more interesting story: the bit-exact replacement of my own Flutter predecessor, built AI-native.",
  tags: ["Swift", "SwiftUI", "macOS", "Swift Concurrency", "CryptoKit", "Flutter", "Migration"],
  image: "/assets/img/work/novatimepuncher.webp",
  articleType: "APP",
  date: "2025-09",
  category: "app",
  platforms: ["macOS", "iOS"],
  applicationCategory: "BusinessApplication",
  appStoreUrl: "https://apps.apple.com/de/app/nt-puncher/id6794961683",
  detailContent: {
    subtitle: "Clock in, clock out and take breaks, as a native app on Mac and iPhone with a glass UI, animated SF Symbols and an actor architecture. And under the hood, a complete stack switch that nobody was supposed to notice.",
    challenge: [
      {
        text: "NT Puncher (spelled out as Novatime Puncher inside the app) is a spare-time project in two acts. Act one: a small Flutter desktop app for clocking in and out against the NovaTime time tracking system, with punch in/out, break tracking and auto-logout. Written long before my AI-native workflow, entirely by hand. It ran in production for years, but it never felt truly at home on the Mac: generic UI, heavyweight runtime, and the old singleton-and-streams code had nothing in common with Swift's modern concurrency world."
      },
      {
        text: "Act two, again in my spare time: rebuild the app natively in SwiftUI, this time AI-native. The catch: it is in daily production use, and when a migration breaks something familiar, it creates questions and distrust. The bar was therefore split in two: behavior, data and color logic had to stay exactly the same, while the look and feel were finally allowed to become properly macOS."
      },
      {
        text: "Two parity requirements were hard constraints, the rest was product decision:"
      },
      {
        listing: [
          "Existing credentials stored in encrypted form must remain transparently readable, so the Flutter app's encryption format is locked in, bit for bit",
          "The color logic of the time displays must stay identical, because a day showing a different color is a bug in the users' eyes",
          "Everything else, from copy to spacing to auto-logout behavior, stays 1:1 on principle, so the switch is invisible"
        ]
      }
    ],
    solution: [
      {
        text: "The core of the implementation is less a UI rewrite than a methodology: the old app becomes the untouchable reference, and every deviation needs a reason. Four building blocks carry that."
      },
      {
        subheadline: "Blueprint instead of gut feeling",
        text: "The complete Flutter app lives in the repo as a read-only reference. It is never edited; it provides the target values: business rules, state transitions, copy, color calculations. A migration spec describes the target in detail, and comparisons against the blueprint replace discussions. The punch state machine (four states, four action codes, including edge cases like “punching out during a break”) was carried over one to one instead of being reinvented."
      },
      {
        subheadline: "Bit-exact crypto compatibility",
        text: "The Flutter app stores credentials AES-GCM-encrypted in its own format: nonce, ciphertext and auth tag as hex strings, separated by slashes. The Swift side implements exactly this format with CryptoKit: existing data is decrypted transparently, new data is written compatibly. It is the only place in the project where bit-level fidelity to a foreign format must be guaranteed, and therefore the place where a silent bug would be most expensive."
      },
      {
        subheadline: "Actors instead of locks",
        text: "The new app uses Swift's Approachable Concurrency consistently: UI code is MainActor-isolated by default, and everything concurrent (API controller, state service, logging, auto-logout scheduler, notifications) is its own actor. Not a single lock, no dispatch queues; data crosses isolation boundaries exclusively and visibly via await. The old approach of singletons and streams simply would not have made it past the compiler under Swift's Sendable checking, so the migration was also a forced architecture upgrade."
      },
      {
        subheadline: "Native also means: feels native",
        text: "This is where the rebuild was allowed to get visibly better: the macOS frosted-glass look (NSVisualEffectView for older systems, the native glassEffect API from macOS 26 on), animated SF Symbols for state changes and consistent design tokens behind shared view modifiers. What Flutter could only imitate, the platform now delivers itself. And for development without a reachable target system there is a complete mock layer as its own actor: identical protocol, realistic state transitions, without real punch requests ever reaching the production system."
      }
    ],
    results: [
      {
        text: "The native app has fully replaced the Flutter predecessor. Nothing familiar broke, and the new version immediately feels right:"
      },
      {
        listing: [
          "1:1 replacement in daily production use: existing credentials, colors, copy and behavior identical",
          "Predecessor's encryption format rebuilt bit-exact in CryptoKit, no migration step needed for user data",
          "Consistent actor architecture without locks: thread safety is guaranteed by the compiler, not by convention",
          "Look natively evolved: glass look with version fallback, animated SF Symbols, genuine macOS feel",
          "Mock layer for fully offline development against realistic state transitions",
          "Universal app: brought to iPhone as a nice-to-have, every view designed for the platform instead of merely stretched"
        ]
      },
      {
        text: "Later the iPhone version was added, more as a nice-to-have because the stack made it easy. Instead of just squeezing the Mac layout into a narrow window, every view is designed specifically for iPhone, so the app feels native there too and not like an afterthought."
      },
      {
        text: "The real takeaway is transferable: a migration has succeeded when nothing familiar breaks and the new version immediately feels right. The blueprint approach, meaning the old app as an untouchable reference, parity as the default and deviation only with a reason, turns a risky rewrite into a controlled, verifiable process. And the project marks a turning point for me: the Flutter predecessor was built entirely without AI, the rebuild AI-native. Same app, same developer, two eras of tooling."
      }
    ],
    technologies: [
      "Swift",
      "SwiftUI",
      "Swift Concurrency (Actors)",
      "CryptoKit",
      "AppKit (NSVisualEffectView)",
      "URLSession",
      "XCTest",
      "Flutter (reference)"
    ],
    codeExamples: [
      {
        language: "swift",
        description: "Bit-exact compatibility with the Flutter format: the stored cipher consists of three hex components (nonce / ciphertext / auth tag). The Swift side reads exactly this format with CryptoKit. Existing data keeps working without a migration step.",
        code: `// CryptoService — reads the cipher format of the Flutter predecessor
// Format: "<nonceHex>/<cipherHex>/<macHex>"
static func decrypt(_ stored: String, key: SymmetricKey) throws -> String {
    let parts = stored.split(separator: "/").map(String.init)
    guard parts.count == 3,
          let nonceData = Data(hex: parts[0]),   // 12-byte IV
          let cipherData = Data(hex: parts[1]),
          let tagData = Data(hex: parts[2])      // 16-byte auth tag
    else { throw CryptoError.malformedCipher }

    let sealedBox = try AES.GCM.SealedBox(
        nonce: try AES.GCM.Nonce(data: nonceData),
        ciphertext: cipherData,
        tag: tagData
    )
    let plain = try AES.GCM.open(sealedBox, using: key)
    return String(decoding: plain, as: UTF8.self)
}`
      },
      {
        language: "swift",
        description: "Approachable Concurrency in practice: UI code is MainActor-isolated via a project setting, services are actors. The compiler enforces that every boundary crossing happens visibly via await. Locks and queues do not appear anywhere in the project.",
        code: `// Services are actors — serial execution is guaranteed by the compiler
actor AutoLogoutScheduler {
    private var task: Task<Void, Never>?

    func schedule(at date: Date, controller: ConnectController) {
        task?.cancel()
        task = Task {
            try? await Task.sleep(until: .now + .seconds(date.timeIntervalSinceNow))
            guard !Task.isCancelled else { return }
            await controller.punch(.logout)   // actor boundary: visible via await
        }
    }
}

// UI stays MainActor-isolated (SWIFT_DEFAULT_ACTOR_ISOLATION = MainActor)
@Observable final class PuncherViewModel {
    private(set) var state: PunchState = .unknown

    func toggle() async {
        state = await controller.togglePunch()  // the only bridge into the service world
    }
}`
      }
    ],
    demoVideo: {
      src: "/assets/video/novatimepuncher_demo.mp4",
      poster: "/assets/img/work/novatimepuncher_demo_poster.webp",
      title: "In the video",
      caption: "NT Puncher in action: clocking in and out, breaks and the weekly overview in the frosted-glass look with animated SF Symbols.",
      width: 800,
      height: 1040
    },
    images: [
      {
        src: "/assets/img/work/novatimepuncher_app.webp",
        alt: "NT Puncher main view with punch-out button, break controls and daily overview",
        type: "image",
        title: "The punch view",
        caption: "Punch out, break controls and daily overview in the frosted-glass look."
      },
      {
        src: "/assets/img/work/novatimepuncher_stats.webp",
        alt: "NT Puncher weekly view with a bar chart per day and weekly total",
        type: "image",
        title: "The weekly view",
        caption: "Core hours, daily bars with 8h and 10h marks, weekly total including an expiry warning above 40 hours. The color rules come 1:1 from the Flutter blueprint."
      }
    ]
  }
};

export default novatimepuncher;
